import type { Deal, Document, ResearchDoc } from "../types/redux";
import { appendFact, setResults } from "../ResultsSlice_Store";
import { setProjectId } from "../ProjectSlice_Store";
import { setLayoutMode } from "../LayoutSlice_Store";
import { getFeatureFlag } from "../hooks/useFeatureFlag";
import { logResultsPayloadDrift } from "../utils/resultsPayloadSchema";
import { chunkAccumulatorsByChunkId } from "./chatModuleState";
import type { ChatDispatch, ChunkAccumulator } from "./chatSliceTypes";

const FACT_LOG_INTERVAL = 10;

interface EvidenceBundle {
  results: unknown[];
  documents: unknown[];
  researchDocs: unknown[];
  facts: unknown[];
}

/**
 * Always dispatch — even when empty — so stale deals/docs from prior turns are
 * cleared. Facts are merged (not replaced) by setResults, which is intentional.
 *
 * When the inline deals flag is ON, only facts (doc Q&A) auto-open the
 * workspace: documents returned alongside deals are reachable from the deal
 * detail panel's Docs tab.
 */
export function dispatchEvidence(
  evidence: EvidenceBundle,
  turnId: string,
  dispatch: ChatDispatch
): void {
  const { results, documents, researchDocs, facts } = evidence;
  const hasResults =
    results.length > 0 || documents.length > 0 || researchDocs.length > 0 || facts.length > 0;

  const inlineDealsFlag = getFeatureFlag("inlineDeals");
  dispatch(setResults({ results, documents, researchDocs, facts, turnId, inlineDealsFlag }));

  if (!hasResults) return;
  const hasNonDealResults = inlineDealsFlag ? facts.length > 0 : true;
  if (hasNonDealResults) {
    dispatch(setLayoutMode("workspace"));
  }
}

/** Concatenate every stored chunk back into one payload, in arrival order. */
function reassemble(accumulator: ChunkAccumulator): EvidenceBundle {
  const assembled: EvidenceBundle = {
    results: [],
    documents: [],
    researchDocs: [],
    facts: [],
  };

  for (const chunk of accumulator.chunks) {
    if (!chunk || !chunk.payload) {
      console.error("❌ [CHUNK] Missing chunk in accumulator during reassembly");
      continue;
    }
    assembled.results.push(...chunk.payload.results);
    assembled.documents.push(...chunk.payload.documents);
    assembled.researchDocs.push(...chunk.payload.researchDocs);
    assembled.facts.push(...chunk.payload.facts);
  }

  return assembled;
}

/** Buffer one slice of an oversized results payload, flushing once complete. */
export function handleResultsPayloadChunk(channelData: any, dispatch: ChatDispatch): void {
  const { chunkId, chunkIndex, chunkCount, sizeBytes, turnId, projectId } = channelData;
  const payload = channelData.payload || {};

  console.log(`📦 [CHUNK] Received chunk ${chunkIndex + 1}/${chunkCount} for chunkId ${chunkId}`);

  if (!chunkAccumulatorsByChunkId.has(chunkId)) {
    chunkAccumulatorsByChunkId.set(chunkId, {
      chunks: [],
      expected: chunkCount,
      receivedBytes: 0,
      turnId,
      projectId,
    });
  }

  const accumulator = chunkAccumulatorsByChunkId.get(chunkId);
  accumulator.chunks[chunkIndex] = {
    chunkIndex,
    payload: {
      results: payload.results || [],
      documents: payload.documents || [],
      researchDocs: payload.researchDocs || [],
      facts: payload.facts || [],
    },
    sizeBytes,
  };
  accumulator.receivedBytes += sizeBytes;

  const receivedCount = accumulator.chunks.filter((chunk) => chunk).length;
  console.log(`📦 [CHUNK] Stored chunk ${chunkIndex}, received ${receivedCount}/${accumulator.expected}`);

  if (receivedCount !== accumulator.expected) return;

  console.log(`✅ [CHUNK] All chunks received for chunkId ${chunkId}, reassembling...`);
  const assembled = reassemble(accumulator);
  console.log(
    `✅ [CHUNK] Reassembled payload: ${assembled.results.length} results, ${assembled.documents.length} docs, ` +
      `${assembled.researchDocs.length} researchDocs, ${assembled.facts.length} facts ` +
      `(${accumulator.receivedBytes} bytes)`
  );

  if (accumulator.chunks.length !== chunkCount) {
    console.error(
      `❌ [CHUNK] Chunk count mismatch: expected ${chunkCount}, got ${accumulator.chunks.length}`
    );
  }

  if (projectId) {
    dispatch(setProjectId(projectId));
  }

  dispatchEvidence(assembled, accumulator.turnId, dispatch);

  chunkAccumulatorsByChunkId.delete(chunkId);
  console.log(`🧹 [CHUNK] Cleaned up accumulator for chunkId ${chunkId}`);
}

/** Trace the fields most often lost between the backend and the deals table. */
function logSampleDeal(results: Deal[]): void {
  console.log("🔍 [RESULTS_PAYLOAD] Raw payload.results:", JSON.stringify(results.slice(0, 2), null, 2));
  if (results.length === 0) return;

  const sampleDeal = results[0];
  console.log("🔍 [RESULTS_PAYLOAD] Sample deal fields:", {
    dealName: sampleDeal.dealName,
    year: sampleDeal.year,
    investmentYear: sampleDeal.investmentYear,
    stage: sampleDeal.stage,
    dealStage: sampleDeal.dealStage,
    team: sampleDeal.team,
    description: sampleDeal.description,
    business: sampleDeal.business,
  });
}

/**
 * Phase 3: evidence (facts, documents, results) goes to the central
 * ResultsSlice; messages only store textual/chat metadata.
 */
export function handleResultsPayload(channelData: any, dispatch: ChatDispatch): void {
  const payload = channelData.payload || {};
  const turnId = channelData.turnId;

  // Run the drift guard at the WS boundary so shape drift surfaces once per
  // payload via structured console.warn (never throws). Use its best-effort
  // normalised arrays for dispatch so a drifted non-array field degrades to []
  // instead of reaching setResults and defeating the "never blocks the UI"
  // contract. The guard returns `unknown[]` — we cast to the reducer-expected
  // shapes, matching the documented ValidationResult caller contract.
  const drift = logResultsPayloadDrift(payload, { turnId, source: "results_payload" });
  const results = drift.results as Deal[];

  logSampleDeal(results);

  if (payload.projectId) {
    dispatch(setProjectId(payload.projectId));
  }

  dispatchEvidence(
    {
      results,
      documents: drift.documents as Document[],
      researchDocs: drift.researchDocs as ResearchDoc[],
      facts: drift.facts,
    },
    turnId,
    dispatch
  );
}

/** Facts stream individually after results/docs so no single frame is oversized. */
export function handleFactsStreamItem(channelData: any, dispatch: ChatDispatch): void {
  const { fact, factIndex, factCount } = channelData;
  if (!fact) return;

  // Log progress periodically (every 10 facts or first/last)
  if (factIndex === 0 || factIndex === factCount - 1 || factIndex % FACT_LOG_INTERVAL === 0) {
    console.log(`📥 [FACT] Received fact ${factIndex + 1}/${factCount}`);
  }

  dispatch(appendFact(fact));

  if (factIndex === factCount - 1) {
    console.log(`✅ [FACT] All ${factCount} facts received`);
  }
}
